import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Button from './Button';
import Invoice from './Invoice';
import { PageMain } from './SkipLink';
import { formatCents } from '../util/bookingPricing';
import AddToCalendar from './AddToCalendar';
import AddToGoogleWallet from './AddToGoogleWallet';
import { BookingCalendarLinks } from '../hooks/useBookingCalendarLinks';

/**
 * The booking confirmation page under the Aperio preset (routes/Success.tsx
 * renders it only when `tenant.themePreset` is `aperio`; every other tenant
 * keeps the page exactly as before).
 *
 * The default page is an app-shell layout: its body is a `flex: 1 1 1px`
 * inner scroll box and the actions a sticky bar, which only works when the
 * page fills the viewport. Under the preset the site's footer shares that
 * flex column, so the body collapsed to nothing, the sticky bar sat alone
 * under the heading, and the map's address pop-up (a portal placed on the
 * collapsed map) floated over the footer (owner, 2026-09-25). This page
 * flows like any page on the site instead: heading, one summary card, what
 * happens next, the actions, then the footer. Its only styling is the
 * `apr-confirm*` rules in public/aperio/theme.css.
 */
export interface AperioConfirmationProps {
  /** A van is assigned: the time is confirmed, not only requested. */
  confirmed: boolean;
  /** The address is outside every service area. */
  outsideArea: boolean;
  /** Booked by staff for a customer (manager, technician, ...). */
  staff: boolean;
  workOrderId: number | null;
  name: string;
  email: string;
  street?: string;
  city?: string;
  provinceCode?: string;
  postalCode?: string;
  /** e.g. "Tuesday, September 29, 2026". */
  dateLabel: string | null;
  /** e.g. "8:00 am - 9:00 am". */
  timeLabel: string;
  /** e.g. "2019 Honda Civic", or empty. */
  vehicleLabel: string;
  isPrepay: boolean;
  prepaid: boolean;
  prepaidAmountCents: number;
  prepayDiscountPercent: number;
  /** Shown when the customer can still pay now (prepay, not paid). */
  onPayNow?: () => void;
  /**
   * "Send the bill to your partner": the line about the emailed pay link
   * (components/PayerPaymentNote), in place of "Pay now" while unpaid.
   */
  payerNote?: ReactNode;
  loggedIn: boolean;
  onBookAnother: () => void;
  onGoToAccount: () => void;
  homeUrl: string | null;
  onGoHome: () => void;
  phone: string;
  phoneHuman: string;
  helloEmail: string;
  /** The account note (log in / set a password), when there is one. */
  accountNote?: ReactNode;
  /**
   * "Add to calendar" links of a confirmed booking (Google, .ics). Shown
   * only to the customer, only once the time is confirmed.
   */
  calendarLinks?: BookingCalendarLinks | null;
  /**
   * "Add to Google Wallet": the member card of a booking that carries a
   * plan (the server gives it only then, and only while the wallet is
   * configured). Shown only to the customer.
   */
  walletSaveUrl?: string | null;
}

export default function AperioConfirmation({
  confirmed,
  outsideArea,
  staff,
  workOrderId,
  name,
  email,
  street,
  city,
  provinceCode,
  postalCode,
  dateLabel,
  timeLabel,
  vehicleLabel,
  isPrepay,
  prepaid,
  prepaidAmountCents,
  prepayDiscountPercent,
  onPayNow,
  payerNote,
  loggedIn,
  onBookAnother,
  onGoToAccount,
  homeUrl,
  onGoHome,
  phone,
  phoneHuman,
  helloEmail,
  accountNote,
  calendarLinks = null,
  walletSaveUrl = null,
}: AperioConfirmationProps) {
  const { t } = useTranslation();
  let title = confirmed ? t('confirm.title.booked') : t('confirm.title.request');
  if (outsideArea) title = t('confirm.title.outside');

  const firstName = (name || '').trim().split(/\s+/)[0];
  const thanks = firstName
    ? t('confirm.thanksName', { name: firstName })
    : t('confirm.thanks');
  let lede: string;
  if (staff) {
    lede = outsideArea
      ? 'This address is outside every service area, so no team was '
        + 'assigned. Check it in the admin panel.'
      : `The booking is saved${confirmed ? ' and assigned' : ''}. The `
        + 'customer gets a confirmation email.';
  } else if (outsideArea) {
    lede = `${thanks} ${t('confirm.lede.outside')}`;
  } else if (confirmed) {
    lede = `${thanks} ${t('confirm.lede.confirmed', { email })}`;
  } else {
    lede = `${thanks} ${t('confirm.lede.requested', { email })}`;
  }

  const addressLine = [street, city].filter(Boolean).join(', ');
  const addressDetail = [provinceCode, postalCode].filter(Boolean).join(' ');

  let payment: ReactNode;
  if (isPrepay && prepaid) {
    payment = (
      <p>
        <strong>
          {prepaidAmountCents > 0
            ? t('confirm.pay.paidAmount', {
              amount: formatCents(prepaidAmountCents),
            })
            : t('confirm.pay.paid')}
        </strong>
        {' '}
        {t('confirm.pay.paidBody', { percent: prepayDiscountPercent })}
      </p>
    );
  } else if (isPrepay && payerNote && !staff) {
    payment = payerNote;
  } else if (isPrepay && staff) {
    payment = (
      <p>
        {`The customer chose to prepay and save ${prepayDiscountPercent}%. `
          + 'They get a payment link by email; if they pay, it is applied to '
          + 'the invoice at completion.'}
      </p>
    );
  } else if (isPrepay) {
    payment = (
      <>
        <p>
          <strong>{t('confirm.pay.notYet')}</strong>
          {' '}
          {t('confirm.pay.notYetBody', { percent: prepayDiscountPercent })}
        </p>
        {onPayNow && (
          <Button onClick={onPayNow}>
            {t('prepay.title', { percent: prepayDiscountPercent })}
          </Button>
        )}
      </>
    );
  } else {
    payment = (
      <p>
        <strong>{t('confirm.pay.atVisit')}</strong>
        {' '}
        {t('confirm.pay.atVisitBody')}
      </p>
    );
  }

  return (
    <PageMain className="apr-confirm" testId="aperio-confirmation">
      <header className="apr-confirm__head">
        <p className="apr-confirm__eyebrow">
          {workOrderId
            ? t('confirm.bookingNumber', { id: workOrderId })
            : t('confirm.booking')}
        </p>
        <h1 className="apr-confirm__title">{title}</h1>
        <p className="apr-confirm__lede" data-mp-mask="">{lede}</p>
      </header>

      <div className="apr-confirm__grid">
        <section
          className="apr-confirm__card"
          aria-labelledby="apr-confirm-summary"
        >
          <h2 id="apr-confirm-summary" className="apr-confirm__h">
            {t('confirm.appointment')}
          </h2>
          <dl className="apr-confirm__facts">
            {addressLine && (
              <div className="apr-confirm__fact">
                <dt>{t('summary.serviceAddress')}</dt>
                <dd data-mp-mask="">
                  {addressLine}
                  {addressDetail && (
                    <span className="apr-confirm__sub">{addressDetail}</span>
                  )}
                </dd>
              </div>
            )}
            {dateLabel && (
              <div className="apr-confirm__fact">
                <dt>{t('schedule.date')}</dt>
                <dd>{dateLabel}</dd>
              </div>
            )}
            {timeLabel && (
              <div className="apr-confirm__fact">
                <dt>
                  {confirmed
                    ? t('schedule.timeShort')
                    : t('confirm.requestedTime')}
                </dt>
                <dd>{timeLabel}</dd>
              </div>
            )}
            {vehicleLabel && (
              <div className="apr-confirm__fact">
                <dt>{t('confirm.vehicle')}</dt>
                <dd>{vehicleLabel}</dd>
              </div>
            )}
          </dl>

          {confirmed && !staff && !outsideArea && calendarLinks && (
            <AddToCalendar links={calendarLinks} aperio headingLevel="h3" />
          )}

          {!staff && walletSaveUrl && (
            <section
              className="apr-wallet"
              aria-labelledby="apr-wallet-title"
              data-testid="apr-wallet"
            >
              <h3
                id="apr-wallet-title"
                className="apr-confirm__h apr-wallet__title"
              >
                {t('confirm.wallet.title')}
              </h3>
              <p className="apr-confirm__fine" id="apr-wallet-body">
                {t('confirm.wallet.body')}
              </p>
              <AddToGoogleWallet href={walletSaveUrl} />
            </section>
          )}

          <Invoice
            className="apr-confirm__invoice"
            showDisclaimer={false}
            condense
          />
          <p className="apr-confirm__fine">{t('confirm.taxEstimate')}</p>

          <div
            className={prepaid
              ? 'apr-confirm__pay apr-confirm__pay--paid'
              : 'apr-confirm__pay'}
          >
            {payment}
          </div>
        </section>

        <aside className="apr-confirm__side">
          {!staff && (
            <section
              className="apr-confirm__next"
              aria-labelledby="apr-confirm-next"
            >
              <h2 id="apr-confirm-next" className="apr-confirm__h">
                {t('confirm.next.title')}
              </h2>
              <ol className="apr-confirm__steps">
                <li data-mp-mask="">
                  <strong>{t('confirm.next.inbox')}</strong>
                  {' '}
                  {t('confirm.next.inboxBody', { email })}
                </li>
                <li>
                  <strong>
                    {confirmed
                      ? t('confirm.next.onTheWay')
                      : t('confirm.next.weConfirm')}
                  </strong>
                  {' '}
                  {confirmed
                    ? t('confirm.next.onTheWayBody')
                    : t('confirm.next.weConfirmBody')}
                </li>
                <li>
                  <strong>{t('confirm.next.wherePark')}</strong>
                  {' '}
                  {t('confirm.next.whereParkBody')}
                </li>
              </ol>
            </section>
          )}
          {accountNote}
          <p className="apr-confirm__help">
            <Trans
              t={t}
              i18nKey="confirm.help"
              values={{ phone: phoneHuman, email: helloEmail }}
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-has-content, max-len, jsx-a11y/control-has-associated-label
                tel: <a href={`tel:${phone}`} />,
                // eslint-disable-next-line jsx-a11y/anchor-has-content, max-len, jsx-a11y/control-has-associated-label
                mail: <a href={`mailto:${helloEmail}`} />,
              }}
            />
          </p>
        </aside>
      </div>

      <div className="apr-confirm__actions">
        <Button onClick={onBookAnother}>{t('confirm.bookAnother')}</Button>
        {loggedIn && (
          <Button variant="secondary" onClick={onGoToAccount}>
            {t('confirm.myAccount')}
          </Button>
        )}
        {homeUrl ? (
          <a className="apr-confirm__home" href={homeUrl}>
            {t('confirm.home')}
          </a>
        ) : (
          <Button variant="tertiary" onClick={onGoHome}>
            {t('confirm.home')}
          </Button>
        )}
      </div>
    </PageMain>
  );
}
